import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import { useLocale } from '../../i18n/LocaleContext';
import {
  BookOpen, Users, GraduationCap, Trophy, Target, BarChart3, Medal, MessageSquare,
  FileText, Smartphone, Ruler, Wrench, ArrowRight, Sun, Moon, BrainCircuit, Video,
  FolderUp, GripVertical, ShieldCheck, SunMoon, Activity, Clock, Layers, TrendingUp,
  PlayCircle, Code, Monitor, Cpu, BookMarked, GitBranch, Calculator, Network, FlaskConical
} from 'lucide-react';

const icons = {
  BookOpen, Users, GraduationCap, Trophy, Target, BarChart3, Medal, MessageSquare,
  FileText, Smartphone, Ruler, Wrench, ArrowRight, Sun, Moon, BrainCircuit, Video,
  FolderUp, GripVertical, ShieldCheck, SunMoon, Activity, Clock, Layers, TrendingUp,
  PlayCircle, Code, Monitor, Cpu, BookMarked, GitBranch, Calculator, Network, FlaskConical
};

const moduleIcon = [Calculator, GitBranch, Network];
const flippedIcon = [PlayCircle, Code];
const methodologyIcon = [Ruler, Wrench, GraduationCap];

function LanguageSwitcher() {
  const { locale, setLocale, locales } = useLocale();
  return (
    <div className="flex items-center gap-1 bg-gray-100 dark:bg-white/10 rounded-lg p-1">
      {locales.map(({ code, label }) => (
        <button
          key={code}
          onClick={() => setLocale(code)}
          className={`px-2 py-1 text-xs font-medium rounded-md transition ${
            locale === code
              ? 'bg-white dark:bg-white/20 text-blue-700 dark:text-blue-300 shadow-sm'
              : 'text-gray-600 dark:text-gray-300 hover:text-blue-700 dark:hover:text-white'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function LandingPage() {
  const [data, setData] = useState({ courses: 0, teachers: 0, students: 0, certificates: 0 });
  const { dark, toggle } = useTheme();
  const { t } = useLocale();

  const nav = t('landing.nav');
  const hero = t('landing.hero');
  const modulesSection = t('landing.modules');
  const flippedSection = t('landing.flipped');
  const stats = t('landing.stats');
  const features = t('landing.features');
  const methodology = t('landing.methodology');
  const cta = t('landing.cta');
  const footer = t('landing.footer');

  const heroTitle = hero.title || [];

  useEffect(() => {
    api.get('/catalog').then(r => {
      setData(prev => ({ ...prev, courses: r.data?.data?.length || r.data?.length || 0 }));
    }).catch(() => { });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 text-gray-900 dark:from-slate-900 dark:via-slate-950 dark:to-indigo-950 dark:text-white">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center text-xl font-bold text-white">
              <FlaskConical className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-700 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">SmartEduLab</h1>
              <p className="text-[10px] text-blue-600/70 dark:text-blue-300/70 -mt-0.5 tracking-wide">{nav.logoSubtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <button onClick={toggle} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-xl transition" title={dark ? 'Kunduzgi rejim' : 'Tungi rejim'}>
              {dark ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-gray-600" />}
            </button>
            <Link to="/login" className="px-5 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-white transition">{nav.login}</Link>
            <Link to="/register" className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg text-sm font-medium text-white hover:opacity-90 transition shadow-lg shadow-blue-600/25">{nav.register}</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-400/20 rounded-full text-sm text-blue-700 dark:text-blue-300 mb-8">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            {hero.badge}
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
            {heroTitle[0]}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent">{heroTitle[1]}</span>
            {heroTitle[2]}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-blue-200/70 max-w-3xl mx-auto mb-10 leading-relaxed">
            {hero.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl text-lg font-bold text-white hover:opacity-90 transition shadow-xl shadow-blue-600/30">
              {hero.ctaStart}
            </Link>
            <Link to="/catalog" className="px-8 py-4 bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/20 rounded-xl text-lg font-medium hover:bg-gray-200 dark:hover:bg-white/20 transition backdrop-blur">
              {hero.ctaCatalog}
            </Link>
          </div>
        </div>
      </section>

      {/* INA Subject Modules */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-blue-50/50 dark:from-slate-900 dark:to-blue-950/30 border-y border-gray-200 dark:border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-100 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-400/20 rounded-full text-xs text-indigo-700 dark:text-indigo-300 mb-4">
              <BookMarked className="w-3.5 h-3.5" />
              {modulesSection.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{modulesSection.title}</h2>
            <p className="text-gray-500 dark:text-blue-200/60 max-w-2xl mx-auto">
              {modulesSection.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {(modulesSection.items || []).map((mod, idx) => {
              const Icon = moduleIcon[idx] || Calculator;
              return (
                <div
                  key={idx}
                  className="group relative bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl p-6 hover:shadow-xl hover:shadow-blue-500/10 dark:hover:shadow-blue-400/10 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                  </div>
                  <h3 className="text-lg font-bold mb-1">{idx + 1}-modul: {mod.title}</h3>
                  <p className="text-sm font-medium text-blue-600 dark:text-blue-300 mb-3">{mod.subtitle}</p>
                  <p className="text-sm text-gray-500 dark:text-blue-200/60 leading-relaxed mb-4">{mod.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {(mod.topics || []).map((topic, i) => (
                      <span key={i} className="px-2.5 py-1 bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-400/20 rounded-md text-xs text-blue-700 dark:text-blue-300">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Flipped Classroom */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-400/20 rounded-full text-xs text-purple-700 dark:text-purple-300 mb-4">
              <Monitor className="w-3.5 h-3.5" />
              {flippedSection.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{flippedSection.title}</h2>
            <p className="text-gray-500 dark:text-blue-200/60 max-w-2xl mx-auto">
              {flippedSection.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {(flippedSection.items || []).map((card, i) => {
              const Icon = flippedIcon[i] || PlayCircle;
              return (
                <div
                  key={i}
                  className="relative overflow-hidden bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 dark:bg-blue-400/5 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                      </div>
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-300">{card.phase}</span>
                        <h3 className="text-xl font-bold">{card.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-blue-200/60 leading-relaxed mb-5">{card.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {(card.tags || []).map((tag, j) => (
                        <span key={j} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-400/20 rounded-lg text-xs font-medium text-blue-700 dark:text-blue-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 border-y border-gray-200 dark:border-white/10 bg-gradient-to-r from-slate-50 via-blue-50/50 to-slate-50 dark:from-slate-900 dark:via-blue-950/30 dark:to-slate-900 backdrop-blur">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 px-6">
          {(stats || []).map((s, i) => {
            const Icon = [Users, Activity, Layers, TrendingUp, PlayCircle, Cpu, BarChart3, Trophy][i] || Users;
            return (
              <div key={i} className="text-center group">
                <div className="mb-3 flex justify-center">
                  <div className="w-14 h-14 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl flex items-center justify-center shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all">
                    <Icon className="w-7 h-7 text-blue-600 dark:text-blue-300" />
                  </div>
                </div>
                <div className="text-3xl font-extrabold bg-gradient-to-r from-blue-700 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">{s.value}</div>
                <div className="text-sm text-gray-500 dark:text-blue-300/70 font-medium mt-1">{s.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('landing.featuresTitle') || 'Platforma imkoniyatlari'}</h2>
            <p className="text-gray-500 dark:text-blue-200/70 max-w-2xl mx-auto">{t('landing.featuresSubtitle') || "Dissertatsiya tadqiqotlari asosida ishlab chiqilgan innovatsion ta'lim vositalari"}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(features || []).map((f, i) => {
              const keys = ['BrainCircuit', 'Target', 'Video', 'FolderUp', 'FileText', 'Medal', 'BarChart3', 'MessageSquare', 'GripVertical', 'SunMoon', 'ShieldCheck', 'Smartphone'];
              const Icon = icons[keys[i]] || BrainCircuit;
              return (
                <div key={i} className="group p-6 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl hover:bg-blue-50 dark:hover:bg-white/10 hover:border-blue-300 dark:hover:border-blue-400/30 transition-all duration-300 shadow-sm dark:shadow-none">
                  <div className="mb-4 group-hover:scale-110 transition-transform"><Icon className="w-10 h-10 text-blue-600 dark:text-blue-300" /></div>
                  <h3 className="text-xl font-bold mb-2">{f.title}</h3>
                  <p className="text-gray-500 dark:text-blue-200/60 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Methodology section */}
      <section className="py-24 px-6 bg-gradient-to-r from-blue-100/50 to-indigo-100/50 dark:from-blue-600/20 dark:to-indigo-600/20 border-y border-gray-200 dark:border-white/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16">{methodology.title}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {(methodology.cards || []).map((card, i) => {
              const Icon = methodologyIcon[i] || Ruler;
              return (
                <div key={i} className="bg-white dark:bg-white/10 p-6 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm dark:shadow-none">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-500/20 rounded-xl flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-blue-600 dark:text-blue-300" /></div>
                  <h3 className="text-lg font-bold mb-2">{card.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-blue-200/60">{card.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-6">{cta.title}</h2>
          <p className="text-gray-500 dark:text-blue-200/70 mb-8">{cta.desc}</p>
          <Link to="/register" className="inline-flex px-10 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl text-lg font-bold text-white hover:opacity-90 transition shadow-xl shadow-blue-600/30">
            {cta.button}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-400 dark:text-blue-300/50">{footer.copyright}</div>
          <div className="text-sm text-gray-400 dark:text-blue-300/50">{footer.subtitle}</div>
        </div>
      </footer>
    </div>
  );
}
